import type {
  ClaimSupportStatus,
  GencowCtx,
  RagEvaluationFixture,
  RagEvaluationRunResult,
  RagIndexHealth,
  RagOperationKind,
  RagOperationMetric,
  RagOperationsSummary,
  RagReindexMode,
  RagReindexPlan,
  SearchScope,
} from "@gencow/core";
import { createGencowProcedureBuilders } from "@gencow/core";
import { sql, type SQL } from "drizzle-orm";
import {
  RAG_BUILTIN_OPERATION_NAMES,
  ragBuiltinInputSchemas,
  ragBuiltinOutputSchemas,
} from "./builtins/rag/schemas.js";
import {
  emitRagOperationMetric,
  mapRagMetricRow,
  rowsFromMetricResult,
  type RagMetricInput,
} from "./rag-operations-metrics.js";

type OperationDbLike = {
  execute(query: SQL): Promise<unknown>;
};

type CorpusRow = {
  corpus: string;
  visibility_scope: SearchScope["visibility"];
  owner_user_id: string | null;
  source_count: number | string;
  section_count: number | string;
  chunk_count: number | string;
};

type JobCountRow = { status: string; count: number | string };
type MetricCountRow = { operation: string; count: number | string };
type LatencyRow = { avg_latency_ms: number | string | null };
type SourcePlanRow = { id: string; chunk_count: number | string };
type LatestJobRow = {
  id: string;
  status: string;
  stage: string;
  updated_at: string | Date;
};

const REINDEX_MODES = new Set<RagReindexMode>([
  "source-changed",
  "section-changed",
  "corpus-policy-changed",
  "full-rebuild",
]);
const procedure = createGencowProcedureBuilders();

function toNumber(value: number | string | null | undefined): number {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function toIsoString(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : String(value);
}

function normalizeVisibility(value: string): SearchScope["visibility"] {
  if (value === "private" || value === "shared" || value === "public") return value;
  throw new Error(`Unsupported visibility: ${value}`);
}

function normalizeOperation(value: string | undefined): RagOperationKind | undefined {
  if (!value) return undefined;
  if (["ingest", "retrieve", "answer", "reindex", "evaluate"].includes(value)) {
    return value as RagOperationKind;
  }
  throw new Error(`Unsupported RAG operation: ${value}`);
}

function normalizeReindexMode(value: string): RagReindexMode {
  if (REINDEX_MODES.has(value as RagReindexMode)) return value as RagReindexMode;
  throw new Error(`Unsupported reindex mode: ${value}`);
}

function scopeFilter(scope: {
  corpus: string;
  visibility: SearchScope["visibility"];
  ownerUserId?: string | null;
}): SQL {
  const ownerFilter =
    scope.ownerUserId == null ? sql`owner_user_id IS NULL` : sql`owner_user_id = ${scope.ownerUserId}`;
  return sql`corpus = ${scope.corpus} AND visibility_scope = ${scope.visibility} AND ${ownerFilter}`;
}

function indexHealth(row: CorpusRow, jobCounts: Record<string, number>): RagIndexHealth {
  if (jobCounts.failed > 0) return "failed";
  if (toNumber(row.source_count) === 0 || toNumber(row.chunk_count) === 0) return "empty";
  if ((jobCounts.queued ?? 0) > 0 || (jobCounts.converting ?? 0) > 0 || (jobCounts.embedding ?? 0) > 0) {
    return "degraded";
  }
  return "ready";
}

function normalizeFixture(value: unknown): RagEvaluationFixture {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("evaluation fixture must be an object");
  }
  const raw = value as Record<string, unknown>;
  const scope = raw.scope as Record<string, unknown> | undefined;
  if (!scope || typeof scope !== "object") throw new Error("evaluation fixture scope is required");

  const fixture: RagEvaluationFixture = {
    name: String(raw.name ?? "").trim(),
    query: String(raw.query ?? "").trim(),
    scope: {
      corpus: String(scope.corpus ?? "").trim(),
      visibility: normalizeVisibility(String(scope.visibility ?? "")),
      ...(typeof scope.ownerUserId === "string" && scope.ownerUserId.trim()
        ? { ownerUserId: scope.ownerUserId.trim() }
        : {}),
    },
    expectedSourceIds: Array.isArray(raw.expectedSourceIds)
      ? raw.expectedSourceIds.filter((item): item is string => typeof item === "string" && item.length > 0)
      : undefined,
    expectedCitationCountMin:
      typeof raw.expectedCitationCountMin === "number"
        ? Math.max(0, raw.expectedCitationCountMin)
        : undefined,
    expectedClaims: Array.isArray(raw.expectedClaims)
      ? raw.expectedClaims.map((claim) => {
          const item = claim as Record<string, unknown>;
          return {
            claim: String(item.claim ?? "").trim(),
            verdict:
              item.verdict === "partial" || item.verdict === "unsupported" ? item.verdict : "supported",
          };
        })
      : undefined,
  };

  if (!fixture.name) throw new Error("evaluation fixture name is required");
  if (!fixture.query) throw new Error(`evaluation fixture ${fixture.name} query is required`);
  if (!fixture.scope.corpus) throw new Error(`evaluation fixture ${fixture.name} scope.corpus is required`);
  return fixture;
}

function expectedStatus(verdict: "supported" | "partial" | "unsupported"): ClaimSupportStatus {
  if (verdict === "partial") return "partially_supported";
  if (verdict === "unsupported") return "insufficient_evidence";
  return "supported";
}

async function mapSummary(db: OperationDbLike, row: CorpusRow): Promise<RagOperationsSummary> {
  const scope = {
    corpus: row.corpus,
    visibility: row.visibility_scope,
    ownerUserId: row.owner_user_id,
  };
  const [jobCounts, metricCounts, latestJobs, latencyRows] = await Promise.all([
    db.execute(sql`
      SELECT status, COUNT(*) AS count
      FROM rag_ingest_jobs
      WHERE ${scopeFilter(scope)}
      GROUP BY status
    `),
    db.execute(sql`
      SELECT operation, COUNT(*) AS count
      FROM rag_operation_metrics
      WHERE ${scopeFilter(scope)}
      GROUP BY operation
    `),
    db.execute(sql`
      SELECT id, status, stage, updated_at
      FROM rag_ingest_jobs
      WHERE ${scopeFilter(scope)}
      ORDER BY updated_at DESC
      LIMIT 1
    `),
    db.execute(sql`
      SELECT AVG(metric_value) AS avg_latency_ms
      FROM rag_operation_metrics
      WHERE ${scopeFilter(scope)}
        AND metric_name = 'latency_ms'
    `),
  ]);

  const jobs = Object.fromEntries(
    rowsFromMetricResult<JobCountRow>(jobCounts).map((item) => [item.status, toNumber(item.count)]),
  );
  const metrics = Object.fromEntries(
    rowsFromMetricResult<MetricCountRow>(metricCounts).map((item) => [item.operation, toNumber(item.count)]),
  );
  const latest = rowsFromMetricResult<LatestJobRow>(latestJobs)[0] ?? null;
  const latency = rowsFromMetricResult<LatencyRow>(latencyRows)[0]?.avg_latency_ms ?? null;

  return {
    corpus: row.corpus,
    visibility: row.visibility_scope,
    ownerUserId: row.owner_user_id,
    sourceCount: toNumber(row.source_count),
    sectionCount: toNumber(row.section_count),
    chunkCount: toNumber(row.chunk_count),
    jobCounts: jobs,
    latestJob: latest
      ? {
          id: latest.id,
          status: latest.status,
          stage: latest.stage,
          updatedAt: toIsoString(latest.updated_at),
        }
      : null,
    recentMetricCounts: metrics,
    averageLatencyMs: latency == null ? null : toNumber(latency),
    indexHealth: indexHealth(row, jobs),
  };
}

async function listSummaries(ctx: GencowCtx, corpus?: string): Promise<RagOperationsSummary[]> {
  const viewer = ctx.auth.requireAuth();
  const corpusFilter = corpus?.trim() ? sql`AND c.corpus = ${corpus.trim()}` : sql``;
  const result = await ctx.unsafeDb.execute(sql`
    SELECT
      c.corpus,
      c.visibility_scope,
      c.owner_user_id,
      COUNT(DISTINCT s.id) AS source_count,
      COUNT(DISTINCT sec.id) AS section_count,
      COUNT(DISTINCT ch.id) AS chunk_count
    FROM rag_corpora c
    LEFT JOIN rag_sources s ON s.corpus = c.corpus
      AND s.visibility_scope = c.visibility_scope
      AND COALESCE(s.owner_user_id, '') = COALESCE(c.owner_user_id, '')
    LEFT JOIN rag_sections sec ON sec.corpus = c.corpus
      AND sec.visibility_scope = c.visibility_scope
      AND COALESCE(sec.owner_user_id, '') = COALESCE(c.owner_user_id, '')
    LEFT JOIN rag_chunks ch ON ch.corpus = c.corpus
      AND ch.visibility_scope = c.visibility_scope
      AND COALESCE(ch.owner_user_id, '') = COALESCE(c.owner_user_id, '')
    WHERE (c.owner_user_id IS NULL OR c.owner_user_id = ${viewer.id})
      ${corpusFilter}
    GROUP BY c.corpus, c.visibility_scope, c.owner_user_id, c.updated_at
    ORDER BY c.updated_at DESC
  `);
  return Promise.all(rowsFromMetricResult<CorpusRow>(result).map((row) => mapSummary(ctx.unsafeDb, row)));
}

async function runEvaluation(ctx: GencowCtx, rawFixtures: unknown[]): Promise<RagEvaluationRunResult> {
  if (!ctx.grounding) throw new Error("ctx.grounding is required for RAG evaluation");
  const fixtures = rawFixtures.map(normalizeFixture);
  const results = [];
  const metricBuckets = new Map<string, { scope: SearchScope; passed: number; failed: number }>();

  for (const fixture of fixtures) {
    const failures: string[] = [];
    const search = await ctx.search("rag_chunks", fixture.query, {
      fields: ["chunk_text", "lexical_text"],
      scope: fixture.scope,
      limit: 20,
    });
    const matchedSourceIds = [
      ...new Set(search.items.map((item) => String(item.row.source_id ?? "")).filter(Boolean)),
    ];
    for (const expectedSourceId of fixture.expectedSourceIds ?? []) {
      if (!matchedSourceIds.includes(expectedSourceId)) failures.push(`missing source ${expectedSourceId}`);
    }

    const expectedClaims = fixture.expectedClaims ?? [];
    const answerNeeded = expectedClaims.length > 0 || fixture.expectedCitationCountMin !== undefined;
    const answer = answerNeeded
      ? await ctx.grounding.answer({
          question: fixture.query,
          scope: fixture.scope,
          claims:
            expectedClaims.length > 0
              ? expectedClaims.map((claim) => ({ claimText: claim.claim }))
              : undefined,
        })
      : null;
    const citationCount = answer?.citations.length ?? search.items.length;
    if (fixture.expectedCitationCountMin !== undefined && citationCount < fixture.expectedCitationCountMin) {
      failures.push(`citation count ${citationCount} < ${fixture.expectedCitationCountMin}`);
    }

    const claimStatuses = expectedClaims.map((claim, index) => {
      const actual = answer?.claims[index]?.status ?? null;
      const expected = expectedStatus(claim.verdict);
      if (actual !== expected) failures.push(`claim "${claim.claim}" expected ${expected}, got ${actual}`);
      return { claim: claim.claim, expected: claim.verdict, actual };
    });
    const ok = failures.length === 0;
    const key = `${fixture.scope.corpus}:${fixture.scope.visibility}:${fixture.scope.ownerUserId ?? ""}`;
    const bucket = metricBuckets.get(key) ?? { scope: fixture.scope, passed: 0, failed: 0 };
    if (ok) bucket.passed += 1;
    else bucket.failed += 1;
    metricBuckets.set(key, bucket);
    results.push({ name: fixture.name, ok, failures, matchedSourceIds, citationCount, claimStatuses });
  }

  const passed = results.filter((result) => result.ok).length;
  await Promise.all(
    [...metricBuckets.values()].map((bucket) =>
      emitRagEvaluationMetrics(ctx.unsafeDb, bucket.scope, bucket.passed, bucket.failed),
    ),
  );
  return {
    ok: passed === results.length,
    total: results.length,
    passed,
    failed: results.length - passed,
    results,
  };
}

async function emitRagEvaluationMetrics(
  db: OperationDbLike,
  scope: SearchScope,
  passed: number,
  failed: number,
): Promise<void> {
  const base = { corpus: scope.corpus, visibility: scope.visibility, ownerUserId: scope.ownerUserId ?? null };
  await emitRagOperationMetric(db, {
    ...base,
    operation: "evaluate",
    metricName: "fixture_pass_count",
    metricValue: passed,
    unit: "count",
  });
  await emitRagOperationMetric(db, {
    ...base,
    operation: "evaluate",
    metricName: "fixture_fail_count",
    metricValue: failed,
    unit: "count",
  });
}

async function planReindex(
  ctx: GencowCtx,
  args: {
    corpus: string;
    visibility: string;
    mode: string;
    reason: string;
    sourceIds?: string[];
    confirmFullRebuild?: boolean;
  },
): Promise<RagReindexPlan> {
  const viewer = ctx.auth.requireAuth();
  const visibility = normalizeVisibility(args.visibility);
  const mode = normalizeReindexMode(args.mode);
  if (mode === "full-rebuild" && args.confirmFullRebuild !== true) {
    throw new Error("full-rebuild requires confirmFullRebuild=true");
  }
  if (
    (mode === "source-changed" || mode === "section-changed") &&
    (!args.sourceIds || args.sourceIds.length === 0)
  ) {
    throw new Error(`${mode} requires sourceIds`);
  }

  const ownerUserId = visibility === "private" ? viewer.id : null;
  const sourceFilter = args.sourceIds?.length
    ? sql`AND s.id IN (${joinSql(
        args.sourceIds.map((id) => sql`${id}`),
        ", ",
      )})`
    : sql``;
  const result = await ctx.unsafeDb.execute(sql`
    SELECT s.id, COUNT(ch.id) AS chunk_count
    FROM rag_sources s
    LEFT JOIN rag_chunks ch ON ch.source_id = s.id
    WHERE s.corpus = ${args.corpus}
      AND s.visibility_scope = ${visibility}
      AND ${ownerUserId == null ? sql`s.owner_user_id IS NULL` : sql`s.owner_user_id = ${ownerUserId}`}
      ${sourceFilter}
    GROUP BY s.id
    ORDER BY s.id
  `);
  const rows = rowsFromMetricResult<SourcePlanRow>(result);
  return {
    corpus: args.corpus,
    visibility,
    ownerUserId,
    mode,
    reason: args.reason,
    sourceIds: rows.map((row) => row.id),
    sourceCount: rows.length,
    estimatedChunkCount: rows.reduce((sum, row) => sum + toNumber(row.chunk_count), 0),
    requiresConfirmation: mode === "full-rebuild",
  };
}

function joinSql(chunks: SQL[], separator: string): SQL {
  if (chunks.length === 0) return sql`true`;
  let current = chunks[0];
  for (const chunk of chunks.slice(1)) current = sql`${current}${sql.raw(separator)}${chunk}`;
  return current;
}

export const ragOperationsProcedures = {
  summary: procedure.query
    .name(RAG_BUILTIN_OPERATION_NAMES.ragOperationsSummary)
    .input(ragBuiltinInputSchemas.ragOperationsSummary)
    .output(ragBuiltinOutputSchemas.ragOperationsSummary)
    .handler(async ({ context: ctx, input: args }) => listSummaries(ctx, args.corpus)),
  metrics: procedure.query
    .name(RAG_BUILTIN_OPERATION_NAMES.ragOperationsMetrics)
    .input(ragBuiltinInputSchemas.ragOperationsMetrics)
    .output(ragBuiltinOutputSchemas.ragOperationsMetrics)
    .handler(async ({ context: ctx, input: args }): Promise<RagOperationMetric[]> => {
      const viewer = ctx.auth.requireAuth();
      const corpusFilter = args.corpus?.trim() ? sql`AND corpus = ${args.corpus.trim()}` : sql``;
      const operation = normalizeOperation(args.operation);
      const operationFilter = operation ? sql`AND operation = ${operation}` : sql``;
      const limit = Math.max(1, Math.min(args.limit ?? 50, 200));
      const result = await ctx.unsafeDb.execute(sql`
        SELECT *
        FROM rag_operation_metrics
        WHERE (owner_user_id IS NULL OR owner_user_id = ${viewer.id})
          ${corpusFilter}
          ${operationFilter}
        ORDER BY recorded_at DESC
        LIMIT ${limit}
      `);
      return rowsFromMetricResult<Parameters<typeof mapRagMetricRow>[0]>(result).map(mapRagMetricRow);
    }),
  evaluate: procedure.mutation
    .name(RAG_BUILTIN_OPERATION_NAMES.ragEvaluationsRun)
    .input(ragBuiltinInputSchemas.ragEvaluationsRun)
    .output(ragBuiltinOutputSchemas.ragEvaluationsRun)
    .handler(async ({ context: ctx, input: args }) => runEvaluation(ctx, args.fixtures)),
  reindexPlan: procedure.query
    .name(RAG_BUILTIN_OPERATION_NAMES.ragReindexPlan)
    .input(ragBuiltinInputSchemas.ragReindexPlan)
    .output(ragBuiltinOutputSchemas.ragReindexPlan)
    .handler(async ({ context: ctx, input: args }) => planReindex(ctx, args)),
} as const;

export function registerRagOperationsRuntime(): void {}

export type { RagMetricInput };
